import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
    Auth,
    authState,
    confirmPasswordReset,
    OAuthProvider,
    SAMLAuthProvider,
    signInWithCustomToken,
    signInWithEmailAndPassword,
    signInWithPopup,
    signOut,
    updatePassword,
    User,
    UserCredential,
    verifyPasswordResetCode,
} from '@angular/fire/auth';
import { Router } from '@angular/router';
import { AumicoCoreLayoutProvider, AumicoCoreProvider, SessionExpirationService } from '@modeso/aumico-lib-core-fe';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import Debug from 'debug';
import { from, map, Observable, of, ReplaySubject, throwError } from 'rxjs';
import { catchError, finalize, first, mergeMap, switchMap, take, tap } from 'rxjs/operators';
import { CreateUserDto } from '../dtos/createUserDto.interface';
import { GetUserDto } from '../dtos/getUserDto.type';
import { UpdateUserDto } from '../dtos/updateUserDto.type';
import { IChangePasswordRequest } from '../interfaces/change-password-request.interface';
import { ILoginRequest } from '../interfaces/login-request.interface';
import { ISetOrResetPasswordRequest } from '../interfaces/set-or-reset-password-request.interface';
import { IUserPreferences } from '../interfaces/user-preferences.interface';
import { IVerifyOOBCode } from '../interfaces/verifyOOBCode.interface';
import { RoutesUtil } from '../util/RoutesUtil';
const debug = Debug('aumico:aumico-project-reports:AuthService');

@UntilDestroy()
@Injectable({
    providedIn: 'root',
})
export class AuthService {
    private user: User | undefined;
    private csrfTokenSubject = new ReplaySubject<string>(1);
    private csrfTokenRequested = false;
    /**
     *
     * @param http the http client instance
     * @param config the configurations for the library
     */
    constructor(
        private readonly auth: Auth,
        private readonly http: HttpClient,
        private readonly coreProvider: AumicoCoreProvider,
        private readonly aumicoCoreLayoutProvider: AumicoCoreLayoutProvider,
        private readonly routesUtil: RoutesUtil,
        private readonly router: Router,
        private sessionExpirationService: SessionExpirationService,
    ) {
        // REV-21869 #162 — warm the csrf token as soon as a user is known, instead of only ever
        // fetching it lazily off the FIRST mutating request. `AuthHandler.intercept()` forkJoins the
        // id token with `getCsrfToken()` before any write (PUT/POST/PATCH/DELETE) is handed to
        // `next.handle()`, so — with no prefetch — the very first write of every app instance (i.e.
        // after every full page load, since this service's in-memory cache does not survive one)
        // pays a full extra network round trip between the moment the UI already confirmed the
        // action (a picker closing, a button disabling) and the moment the request actually leaves
        // the page. In that window the trustee is free to act again — click a tab, follow a link —
        // with nothing on screen saying the write is still in flight. Firing the same idempotent
        // `getCsrfToken()` here lets that round trip happen in parallel with the rest of the
        // session's bootstrap, so by the time a real write is dispatched the token is already
        // cached and the interceptor's forkJoin resolves immediately. Errors are swallowed here on
        // purpose: `getCsrfToken()` already resets its own state on failure so a later, real caller
        // gets a fresh attempt instead of a poisoned cache.
        this.getCsrfToken()
            .pipe(untilDestroyed(this))
            .subscribe({ error: () => undefined });
    }

    // ────────────────────────────────────────────────────────────────────────────
    // 1) Login with OIDC (e.g. “oidc.auth0”)
    // ────────────────────────────────────────────────────────────────────────────
    /**
     * @param providerId  The provider ID you registered in Firebase Auth,
     *                    e.g. "oidc.auth0" if you wired up Auth0 under Google Identity Platform.
     * @returns an Observable<UserCredential> that emits once sign-in completes.
     */
    loginWithOidc(providerId: string): Observable<UserCredential> {
        const oidcProvider = new OAuthProvider(providerId);
        oidcProvider.addScope('openid');
        oidcProvider.addScope('profile');
        oidcProvider.addScope('email');

        return from(signInWithPopup(this.auth, oidcProvider)).pipe(
            switchMap((userCred: UserCredential) => {
                return from(userCred.user.getIdTokenResult()).pipe(map((tokenResult) => ({ userCred, tokenResult })));
            }),

            switchMap(({ userCred, tokenResult }) => {
                const claims = tokenResult.claims as {
                    roles?: string[];
                    organizationId?: string;
                };

                if (!claims.roles || !claims.organizationId) {
                    const url = this.routesUtil.initSsoAccount();
                    return this.http
                        .post<{ token: string }>(url, {
                            // Note: Changed return type
                            idToken: tokenResult.token,
                        })
                        .pipe(
                            // Use the fresh token to sign in again
                            switchMap((response) => from(signInWithCustomToken(this.auth, response.token))),
                            // Return the original UserCredential (now with updated claims)
                            map(() => userCred),
                        );
                }

                return of(userCred);
            }),

            catchError((err) => {
                console.error('OIDC login failed:', err);
                return throwError(() => err);
            }),
        );
    }
    // ────────────────────────────────────────────────────────────────────────────
    // 2) Login with SAML (e.g. “saml.okta”)
    // ────────────────────────────────────────────────────────────────────────────
    /**
     * @param providerId  The provider ID you registered in Firebase Auth,
     *                    e.g. "saml.okta" if you wired up Okta under Google Identity Platform.
     * @returns an Observable<UserCredential> that emits once sign-in completes.
     */
    loginWithSaml(providerId: string): Observable<UserCredential> {
        const samlProvider = new SAMLAuthProvider(providerId);

        return from(signInWithPopup(this.auth, samlProvider)).pipe(
            switchMap((userCred: UserCredential) => {
                return from(userCred.user.getIdTokenResult()).pipe(map((tokenResult) => ({ userCred, tokenResult })));
            }),

            switchMap(({ userCred, tokenResult }) => {
                const claims = tokenResult.claims as {
                    roles?: string[];
                    organizationId?: string;
                };

                if (!claims.roles || !claims.organizationId) {
                    const url = this.routesUtil.initSsoAccount();
                    return this.http
                        .post<{ token: string }>(url, {
                            // Changed return type
                            idToken: tokenResult.token,
                        })
                        .pipe(
                            // Use the fresh token to sign in again
                            switchMap((response) => from(signInWithCustomToken(this.auth, response.token))),
                            // Return the original UserCredential (now with updated claims)
                            map(() => userCred),
                        );
                }

                return of(userCred);
            }),

            catchError((err) => {
                console.error('SAML login failed:', err);
                return throwError(() => err);
            }),
        );
    }

    getStartSsoUrl(handle: string): string {
        // This method should return the URL to start the SSO flow, e.g. "/auth/sso/auth0/start"
        return this.routesUtil.startSsoUrl(handle);
    }

    /**
     * 1) Call backend GET /auth/sso/:handle/callback?code=…
     *    which returns { token: '<firebaseCustomToken>' }.
     * 2) Then call signInWithCustomToken(...) under the covers.
     */
    handleSsoCallback$(handle: string, code: string, state: string): Observable<void> {
        const url = this.routesUtil.exchangeSsoCodeWithToken(handle);

        // 1) Fetch { token } from your backend
        return this.http
            .post<{ token: string }>(url, {
                code: code,
                state: state,
            })
            .pipe(
                switchMap((resp) => {
                    const customToken = resp.token;
                    // 2) Sign in with the Firebase custom token
                    return this.handleFirebaseResponse$(customToken);
                }),
            );
    }

    /**
     * Convert the Promise returned by signInWithCustomToken(...) → Observable<void>.
     */
    handleFirebaseResponse$(token: string): Observable<void> {
        return from(signInWithCustomToken(this.auth, token)).pipe(
            // We don’t care about the return value—just completion
            map(() => void 0),
        );
    }
    getIdToken(): Observable<string> {
        if (!this.auth.currentUser) {
            return throwError(() => new Error('No authenticated user'));
        }
        return from(this.auth.currentUser.getIdToken());
    }

    getCsrfToken(): Observable<string> {
        if (!this.csrfTokenRequested) {
            this.csrfTokenRequested = true;
            // Wait for the first non-null auth state before fetching the token.
            // This prevents errors on pages like login where no user is signed in yet.
            authState(this.auth)
                .pipe(
                    first((user) => user !== null),
                    mergeMap(() => this.getIdToken()),
                    mergeMap((token) => this.getCsrfTokenBackend(token)),
                    untilDestroyed(this),
                )
                .subscribe({
                    next: (csrfToken) => {
                        this.csrfTokenSubject.next(csrfToken);
                        this.csrfTokenSubject.complete();
                    },
                    error: (err) => {
                        // Signal all current waiters with the error, then reset
                        // so the next call can try again. The error() call must
                        // happen on the same subject that was returned to callers
                        // before creating a new one for future requests.
                        this.csrfTokenSubject.error(err);
                        this.csrfTokenRequested = false;
                        this.csrfTokenSubject = new ReplaySubject<string>(1);
                    },
                });
        }
        return this.csrfTokenSubject.asObservable();
    }

    resetCsrfToken(): void {
        this.csrfTokenRequested = false;
        this.csrfTokenSubject = new ReplaySubject<string>(1);
    }

    login$(user: { payload: ILoginRequest }): Observable<User> {
        this.coreProvider.dispatchBlockingRequest('UsersFe_login$');
        const username = user.payload.username.trim();
        return from(signInWithEmailAndPassword(this.auth, username, user.payload.password)).pipe(
            take(1),
            mergeMap((userCredential) => {
                this.user = userCredential.user;
                return of(this.user);
            }),

            finalize(() => this.coreProvider.dispatchBlockingRequestEnd('UsersFe_login$')),
        );
    }

    getCurrentUser$(): Observable<GetUserDto> {
        const url = this.routesUtil.getCurrentUserUrl();

        this.coreProvider.dispatchBlockingRequest('UsersFe_getCurrentUser$');
        return this.http
            .get<GetUserDto>(url)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('UsersFe_getCurrentUser$')));
    }

    updateCurrentUser$(user: { payload: UpdateUserDto }): Observable<void> {
        const url = this.routesUtil.getCurrentUserUrl();
        this.coreProvider.dispatchBlockingRequest('UsersFe_updateCurrentUser$');
        return this.http
            .patch<void>(url, user.payload)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('UsersFe_updateCurrentUser$')));
    }

    logout$(): Observable<void> {
        this.coreProvider.dispatchBlockingRequest('UsersFe_logout$');
        return from(signOut(this.auth)).pipe(
            finalize(() => {
                this.sessionExpirationService.logoutCleanup();
                this.coreProvider.dispatchBlockingRequestEnd('UsersFe_logout$');
            }),
        );
    }

    getCsrfTokenBackend(token: string): Observable<string> {
        const url = this.routesUtil.getCsrfToken();

        return this.http
            .post(url, undefined, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                responseType: 'text',
                withCredentials: true,
            })
            .pipe(
                catchError((errorResponse) => {
                    const userLanguage = localStorage.getItem('currentLanguage') || 'de-ch';
                    if (errorResponse.status === 401) {
                        this.logout$()
                            .pipe(
                                tap((_) => {
                                    this.coreProvider.dispatchResetOverlay();
                                    this.aumicoCoreLayoutProvider.resetLayout();
                                    this.router.navigateByUrl(`${userLanguage}/auth/login`);
                                }),
                                first(),
                                untilDestroyed(this),
                            )
                            .subscribe();
                    }
                    throw errorResponse;
                }),
            );
    }

    getCurrentUserRoles(): Observable<string[]> {
        const user = this.auth.currentUser;
        // 1. Immediate safety check for null user
        if (!user) {
            return of([]);
        }

        // 2. Fetch the token and handle your conditions inside the pipe
        return from(user.getIdTokenResult()).pipe(
            map((result) => {
                const roles = result.claims['roles'] as string[] | undefined;

                // HANDLE YOUR OTHER CONDITION HERE
                // Example: If a user has a 'suspended' claim, return empty even if they have roles
                //if (result.claims['isSuspended']) {
                //    return [];
                //}

                // Return the roles if they exist, otherwise an empty array
                return roles ?? [];
            }),
            catchError((err) => {
                console.error("Error fetching token claims:", err);
                return of([]); // Safety fallback if the network call fails
            })
        );
    }

    signup$(createUserDto: CreateUserDto): Observable<void> {
        const url = this.routesUtil.getUsersUrl();
        this.coreProvider.dispatchBlockingRequest('UsersFe_signup$');
        return this.http
            .post<void>(url, createUserDto)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('UsersFe_signup$')));
    }

    resendVerificationMail(email: string): Observable<void> {
        const url = this.routesUtil.getResendVerificationMailUrl();
        this.coreProvider.dispatchBlockingRequest('UsersFe_verificationMail$');
        return this.http
            .post<void>(url, { email })
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('UsersFe_verificationMail$')));
    }

    setPassword$(setPassword: ISetOrResetPasswordRequest): Observable<void> {
        this.coreProvider.dispatchBlockingRequest('UsersFe_setPassword$');

        const confirmPasswordResetPromise = confirmPasswordReset(this.auth, setPassword.oobCode, setPassword.password);
        return from(confirmPasswordResetPromise).pipe(
            mergeMap(() => {
                return signInWithEmailAndPassword(this.auth, setPassword.email, setPassword.password);
            }),
            mergeMap(() => {
                const url = this.routesUtil.confirmUserSigningUpUrl();
                return this.http.post<void>(url, undefined);
            }),
            finalize(() => this.coreProvider.dispatchBlockingRequestEnd('UsersFe_setPassword$')),
        );
    }
    sendResetPasswordEmail$(email: string): Observable<void> {
        const url = this.routesUtil.getResetPasswordRoute();
        this.coreProvider.dispatchBlockingRequest('UsersFe_sendResetPasswordEmail$');
        return this.http
            .post<void>(url, { email })
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('UsersFe_sendResetPasswordEmail$')));
    }
    resetPassword$(setPassword: ISetOrResetPasswordRequest): Observable<User> {
        this.coreProvider.dispatchBlockingRequest('UsersFe_resetPassword$');
        const confirmPasswordResetPromise = confirmPasswordReset(this.auth, setPassword.oobCode, setPassword.password);
        return from(confirmPasswordResetPromise).pipe(
            mergeMap(() => {
                const signInPromise = signInWithEmailAndPassword(this.auth, setPassword.email, setPassword.password);
                return from(signInPromise);
            }),
            mergeMap((userCredential: UserCredential) => {
                this.user = userCredential.user;
                return of(this.user);
            }),

            finalize(() => this.coreProvider.dispatchBlockingRequestEnd('UsersFe_resetPassword$')),
        );
    }

    changePassword$(changePasswordRequest: IChangePasswordRequest): Observable<User> {
        this.coreProvider.dispatchBlockingRequest('UsersFe_changePassword$');
        const signInWithEmailAndCurrentPasswordPromise = signInWithEmailAndPassword(
            this.auth,
            this.auth.currentUser.email,
            changePasswordRequest.oldPassword,
        );
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return from(signInWithEmailAndCurrentPasswordPromise).pipe(
            mergeMap(() => {
                return updatePassword(this.auth.currentUser, changePasswordRequest.newPassword);
            }),
            mergeMap(() => {
                this.user = this.auth.currentUser;
                return of(this.user);
            }),
            finalize(() => this.coreProvider.dispatchBlockingRequestEnd('UsersFe_changePassword$')),
        );
    }

    verifyOOB$(verifyOOB: IVerifyOOBCode): Observable<string> {
        const verifyPasswordResetCodePromise = verifyPasswordResetCode(this.auth, verifyOOB.oobCode);
        return from(verifyPasswordResetCodePromise);
    }

    agreeToTerms$(): Observable<void> {
        this.coreProvider.dispatchBlockingRequest('agreeToTerms');
        const url = this.routesUtil.getAgreeToTermsUrl();
        return this.http.patch<void>(url, {}).pipe(
            finalize(() => {
                this.coreProvider.dispatchBlockingRequestEnd('agreeToTerms');
            }),
        );
    }

    public updateUserPreferences$(preferences: IUserPreferences): Observable<void> {
        const url = this.routesUtil.getUpdateUserPreferencesUrl();
        return this.http.patch<void>(url, preferences);
    }
}
